import React, { useEffect, useState, useRef, useMemo } from 'react';
import { router }                from '@inertiajs/react';
import axios                      from 'axios';
import CreditsInfo from '@/Components/CreditsInfo';
import GeneratedDraftsPanel       from './GeneratedDraftsPanel';
import GenerationModal            from './GenerationModal';
import Modal from 'bootstrap/js/dist/modal';
import { isDone, isFailed, isSettled, isRunning } from './utils';

export default function Editor(props) {
  /* ---------- props from controller ---------- */
  const {
    movieHash,
    title,
    scenesList,
    currSceneNum,
    currSceneText,
    strengths, weaknesses, critique,
    summaries,
    characters,
    suggestions,
    ratingsOriginal,
    credits
  } = props;

  /* ---------- local state ---------- */
  const [sceneText, setSceneText] = useState(currSceneText);
  const [userBackstory, setUserBackstory] = useState('');
  const [userPrompt, setUserPrompt] = useState('');
  const [genState, setGenState] = useState(null);
  const [originalSnapshot, setSnapshot] = useState('');
  const [suggestionStates, setSuggestionStates] = useState(
    suggestions.map(s => ({ text: s, checked: true }))
  );
  const [drafts, setDrafts] = useState([]);
  const [queuingProvider, setQueuingProvider] = useState(null); // ⭐️ FIX: State to track the in-progress generation request.

  const [charData, setCharData] = useState(characters);
  const creditsInfoRef = useRef(null);

  /* ---------- fetch credits function ---------- */
  const refreshCredits = async () => {
    if (creditsInfoRef.current) {
      try {
        await creditsInfoRef.current.refreshCredits();
      } catch (error) {
        console.error('Error refreshing credits:', error);
      }
    }
  };

  /* ---------- cross-component event: Use as Draft ---------- */
  useEffect(() => {
    const h = e => setSceneText(e.detail);
    window.addEventListener('useDraft', h);
    return () => window.removeEventListener('useDraft', h);
  }, []);

  /* ---------- fetch drafts once when scene changes ---------- */
  useEffect(() => {
    axios.get(`/scene-generation/${movieHash}/${currSceneNum}/generations`)
         .then(res => {
           setDrafts(res.data);
         })
         .catch(err => console.error('Error fetching drafts:', err));
  }, [movieHash, currSceneNum]);


  /* -------------------- CONSOLIDATED POLLING LOGIC -------------------- */
  const pollCallback = useRef();

  useEffect(() => {
    // Define the async polling logic inside an effect to always have fresh state.
    pollCallback.current = async () => {
      // Only poll drafts that can still change. A failed draft is finished — polling it
      // forever is what kept the spinner alive on row 23392 from 2026-09-12.
      const stillIncomplete = drafts.filter(d => isRunning(d.status));
      if (stillIncomplete.length === 0) return;

      console.log(`Checking status for ${stillIncomplete.length} drafts via batch request.`);
      try {
        const uuidsToCheck = stillIncomplete.map(d => d.uuid);
        const batchStatusRes = await axios.post('/scene-generation/generations/status-batch', { uuids: uuidsToCheck });
        
        // Failures first, and they need no second request: there is no scene to fetch.
        // Writing the terminal status into local state is what lets shouldPoll go false.
        const newlyFailed = batchStatusRes.data.filter(s => isFailed(s.status));
        if (newlyFailed.length > 0) {
          console.log(`${newlyFailed.length} draft(s) failed`);
          setDrafts(prev => prev.map(d =>
            newlyFailed.some(f => f.uuid === d.uuid) ? { ...d, status: 'error' } : d
          ));
        }

        const newlyCompletedUuids = batchStatusRes.data
            .filter(s => isDone(s.status))
            .map(s => s.uuid);

        if (newlyCompletedUuids.length > 0) {
          console.log(`${newlyCompletedUuids.length} drafts completed, fetching full data`);
          const dataPromises = newlyCompletedUuids.map(uuid =>
            axios.get(`/scene-generation/generations/${uuid}`)
          );
          
          const fullResults = await Promise.all(dataPromises);
          const completedData = fullResults.map(res => res.data);

          setDrafts(prev => {
            const updated = [...prev];
            completedData.forEach(data => {
              const index = updated.findIndex(d => d.uuid === data.uuid);
              if (index !== -1) {
                updated[index] = data;
              }
            });
            return updated;
          });
          refreshCredits();
        }
      } catch (err) {
        console.error('Error in polling loop:', err);
      }
    };
  });

  /* Main polling interval setup */
  useEffect(() => {
    const shouldPoll = drafts.some(d => isRunning(d.status));
    if (!shouldPoll) {
      return; // Nothing can still change - stop. Includes drafts that FAILED.
    }

    console.log('Starting main polling interval.');
    const intervalId = setInterval(() => {
      if (pollCallback.current) {
        pollCallback.current();
      }
    }, 3000);

    return () => {
      console.log('Cleaning up main polling interval.');
      clearInterval(intervalId);
    };
  }, [drafts.some(d => isRunning(d.status))]);
  
  /* Effect to sync the modal's state from the main drafts list */
  useEffect(() => {
    if (genState?.uuid && isRunning(genState.status)) {
      const updatedDraft = drafts.find(d => d.uuid === genState.uuid);
      // Sync on ANY terminal status, not just success - otherwise the open modal keeps
      // spinning even after the list below it has given up.
      if (updatedDraft && isSettled(updatedDraft.status)) {
        console.log(`Syncing modal state for ${updatedDraft.status} draft ${updatedDraft.uuid}`);
        setGenState({
          uuid: updatedDraft.uuid,
          status: updatedDraft.status,
          data: isDone(updatedDraft.status) ? updatedDraft : null
        });
      }
    }
  }, [drafts]);

  /* Effect to sync React state when the Bootstrap modal is hidden. */
  useEffect(() => {
    const modalEl = document.getElementById('generationModal');
    if (!modalEl) return;

    const handleModalHide = () => {
      setGenState(null);
    };

    modalEl.addEventListener('hidden.bs.modal', handleModalHide);

    return () => {
      modalEl.removeEventListener('hidden.bs.modal', handleModalHide);
    };
  }, []);

  /* Declaratively control modal visibility based on state. */
  useEffect(() => {
    const modalEl = document.getElementById('generationModal');
    if (!modalEl) return;
    const modalInstance = Modal.getOrCreateInstance(modalEl);

    if (genState?.status === 'queued') {
      modalInstance.show();
    }
  }, [genState]);


  /* ---------- Prepare the combined list of drafts for the child panel ---------- */
  const gensForPanel = useMemo(() => {
    const originalRow = {
      uuid: 'original',
      isOriginal: true,
      created_at: null,
      scene: null,
      comments: null,
      ratings: ratingsOriginal,
      status: 'complete',
    };
    return [originalRow, ...drafts];
  }, [drafts, ratingsOriginal]);
  
  /* ---------- change scene dropdown ---------- */
  const changeScene = num =>
    router.visit(`/scene-generation/${movieHash}/${num}`);

  /* ---------- credit check ---------- */
  const haveCredit = cost => {
    if (credits < cost) {
      bootstrap.Modal.getOrCreateInstance(
        document.getElementById('creditsModal')).show();
      return false;
    }
    return true;
  };

  /* ---------- launch generation ---------- */
  const generate = apiProvider => {
    const cost = (apiProvider === 'dsr' ? 5 : 2);
    if (!haveCredit(cost)) return;

    setQueuingProvider(apiProvider); // ⭐️ FIX: Set loading state for the clicked provider.

    const selectedSuggestions = suggestionStates
      .filter(s => s.checked && s.text.trim() !== '')
      .map(s => s.text.trim());
    const summariesString = summaries.join('\n') + '\n' + userBackstory;
    const suggestionsString = selectedSuggestions.join('\n') + '\n' + userPrompt;
    const charactersString = JSON.stringify(charData);

    const data = {
      api_provider: apiProvider,
      scene_text: sceneText,
      summaries_string: summariesString,
      suggestions_string: suggestionsString,
      characters_string: charactersString,
    };

    axios.post(`/scene-generation/${movieHash}/${currSceneNum}/generations`, data)
      .then(res => {
        console.log('queued id', res.data.uuid);
        setSnapshot(sceneText);
        setGenState({ uuid: res.data.uuid, status: 'queued', data: null });
        
        setDrafts(prev => [...prev, {
          uuid: res.data.uuid,
          status: 'queued',
          created_at: new Date().toISOString(),
          scene: null,
          comments: null,
          ratings: null
        }]);
      })
      .catch(err => alert('Error queueing generation:\n' + err))
      .finally(() => {
        setQueuingProvider(null); // ⭐️ FIX: Reset loading state regardless of success or error.
      });
  };
  
  /* ---------- render ---------- */
  return (
    <div className="container-fluid">
      {/* header bar */}
      <div className="my-2">
        <CreditsInfo
          ref={creditsInfoRef}
          movie_title={title}
          movie_hash={movieHash}
          initialCredits={credits}
          user_email={props.user_email}
        />
      </div>
      <div className="d-flex justify-content-between align-items-center my-2">
        <div>
          <select className="form-select d-inline w-auto me-3"
                  value={currSceneNum}
                  onChange={e => changeScene(e.target.value)}>
            {scenesList.map(s => (
              <option key={s.number} value={s.number}>
                {s.number} – {s.title}
              </option>
            ))}
          </select>
        </div>
      </div>
      <h1 className="text-center bg-secondary text-white py-2 mb-3">
        Improve Scene "{title}"
      </h1>
      
      <GeneratedDraftsPanel
        gens={gensForPanel}
        originalRatings={ratingsOriginal}
      />
      
      {/* info section – strengths / weaknesses / critique */}
      <div className="row g-3 mb-4">
        <InfoCard title="Strengths" body={strengths} col="6" />
        <InfoCard title="Weaknesses" body={weaknesses} col="6" />
        <InfoCard title="Critique" body={critique} col="12" />
      </div>
      
      {/* main edit area */}
      <div className="row">
        {/* left: textarea */}
        <div className="col-md-6">
          <CardHeader>Scene Text</CardHeader>
          <textarea className="form-control" rows="30"
                    value={sceneText}
                    onChange={e => setSceneText(e.target.value)} />
        </div>
        
        {/* right panels */}
        <div className="col-md-6">
          <CardHeader>What happened before</CardHeader>
          <div className="mb-3">
            {summaries.map((s, i) => (
              <textarea key={i} className="form-control mb-2" rows="3"
                        defaultValue={s} />
            ))}
            <textarea className="form-control"
                       placeholder="Additional backstory"
                      rows="4"
                      value={userBackstory}
                      onChange={e => setUserBackstory(e.target.value)} />
          </div>
          
          <CardHeader>Characters</CardHeader>
          <div className="row">
            {Object.entries(charData).map(([name, desc]) => (
              <div className="col-6 mb-3" key={name}>
                <div className="form-check mb-1">
                  <input className="form-check-input" type="checkbox" defaultChecked />
                  <label className="form-check-label fw-bold">{name}</label>
                </div>
                <textarea className="form-control" rows="5"
                          value={desc}
                          onChange={e => setCharData({ ...charData, [name]: e.target.value })} />
              </div>
            ))}
          </div>
          
          <CardHeader>Suggestions</CardHeader>
          {suggestionStates.map((sugg, i) => (
            <div key={i} className="form-check mb-2">
              <input
                className="form-check-input"
                type="checkbox"
                id={`suggestion-${i}`}
                checked={sugg.checked}
                onChange={e => {
                  const updated = [...suggestionStates];
                  updated[i].checked = e.target.checked;
                  setSuggestionStates(updated);
                }}
              />
              <textarea
                className="form-control"
                rows="2"
                value={sugg.text}
                onChange={e => {
                  const updated = [...suggestionStates];
                  updated[i].text = e.target.value;
                  setSuggestionStates(updated);
                }}
              />
            </div>
          ))}
          <textarea className="form-control"
                    placeholder="Add your own prompt"
                    rows="4"
                    value={userPrompt}
                    onChange={e => setUserPrompt(e.target.value)} />
        </div>
      </div>
      
      {/* ⭐️ FIX: generate buttons now show loading state and are disabled during queueing. */}
      <div className="text-end my-4">
        <button onClick={() => generate('openai')}
                className="btn btn-primary me-2"
                disabled={queuingProvider !== null}>
          {queuingProvider === 'openai' ? (
            <><i className="fa fa-spinner fa-spin me-2" />Queuing...</>
          ) : 'GPT (2 credits)'}
        </button>
        <button onClick={() => generate('claude_sonnet_4_6')}
                className="btn btn-secondary me-2"
                disabled={queuingProvider !== null}>
          {queuingProvider === 'claude_sonnet_4_6' ? (
            <><i className="fa fa-spinner fa-spin me-2" />Queuing...</>
          ) : 'Claude Sonnet (2 credits)'}
        </button>
        <button onClick={() => generate('dsc')}
                className="btn btn-success me-2"
                disabled={queuingProvider !== null}>
          {queuingProvider === 'dsc' ? (
            <><i className="fa fa-spinner fa-spin me-2" />Queuing...</>
          ) : 'DeepSeek (2 credits)'}
        </button>
        <button onClick={() => generate('dsr')}
                className="btn btn-warning"
                disabled={queuingProvider !== null}>
          {queuingProvider === 'dsr' ? (
            <><i className="fa fa-spinner fa-spin me-2" />Queuing...</>
          ) : 'DeepSeek Reasoning (5 credits)'}
        </button>
      </div>
      
      <GenerationModal
        originalText={originalSnapshot}
        originalRatings={ratingsOriginal}
        genState={genState}
      />
    </div>
  );
}

/* ---------- tiny reusable helpers ---------- */
const CardHeader = ({ children }) => (
  <h2 className="bg-primary text-white text-center py-1">{children}</h2>
);

const InfoCard = ({ title, body, col }) => {
  let content;
  try {
    const parsed = JSON.parse(body);
    if (Array.isArray(parsed)) content = <ul>{parsed.map((x, i) => <li key={i}>{x}</li>)}</ul>;
    else if (typeof parsed === 'object') content = <pre>{JSON.stringify(parsed, null, 2)}</pre>;
    else content = <p>{parsed}</p>;
  } catch {
    content = <p>{body}</p>;
  }

  return (
    <div className={`col-md-${col}`}>
      <div className="card h-100">
        <div className="card-header bg-primary text-white">{title}</div>
        <div className="card-body">{content}</div>
      </div>
    </div>
  );
};